import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { useTheme } from '../context/ThemeContext';
import './Dashboard.css';

interface UserStats {
  totalSubmissions: number;
  approvedSubmissions: number;
  rejectedSubmissions: number;
  pendingSubmissions: number;
  totalRewardEarned: number;
}

interface ContestReward {
  id: string;
  type: 'contest' | 'discount';
  title: string;
  description: string;
  pixRequired: number;
  startAt: Timestamp | number;
  endAt: Timestamp | number;
  isActive: boolean;
  partnerName?: string;
  partnerLogoUrl?: string;
  discountCode?: string;
  externalUrl?: string;
}

interface Challenge {
  id: string;
  title: string;
  imageUrl?: string;
  startAt: Timestamp | number;
  endAt: Timestamp | number;
  status: 'scheduled' | 'active' | 'ended';
  requiredCount: number;
  pixReward: number;
  eligible?: {
    categories?: string[];
    subcategories?: string[];
    taskIds?: string[];
  };
}

interface ChallengeProgress {
  count: number;
  requiredCount: number;
  status: 'in_progress' | 'completed' | 'rewarded';
}

const timeFilters = ['This Month', '3 Months', '6 Months', '12 Months'];
const taskFilters = ['All Tasks', 'Photo Tasks', 'Video Tasks'];

export default function Dashboard() {
  const { colors, theme } = useTheme();
  const [timeFilter, setTimeFilter] = useState(timeFilters[0]);
  const [taskFilter, setTaskFilter] = useState(taskFilters[0]);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [contests, setContests] = useState<ContestReward[]>([]);
  const [contestsLoading, setContestsLoading] = useState(true);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [challengesLoading, setChallengesLoading] = useState(true);
  const [challengeProgress, setChallengeProgress] = useState<Record<string, ChallengeProgress>>({});

  useEffect(() => {
    loadSubmissions();
    loadContests();
    loadChallenges();
  }, []);

  useEffect(() => {
    if (challenges.length > 0 && auth.currentUser) {
      loadChallengeProgress();
    }
  }, [challenges, auth.currentUser]);

  const loadSubmissions = async () => {
    if (!auth.currentUser) return;

    setLoading(true);
    try {
      // Fetch from users/{uid} like mobile app does
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      const data = userSnap.data();
      
      if (!data) {
        setSubmissions([]);
        setLoading(false);
        return;
      }

      const completedTasks = data.completedTasks || {};
      
      // Convert completedTasks to an array of submissions (same as mobile app)
      const arr = Object.entries(completedTasks).flatMap(([taskId, value]: any) => {
        // Check if value has a submissions array, if not, create one with the current value
        const taskSubmissions = Array.isArray(value.submissions) ? value.submissions : [value];
        
        return taskSubmissions.map((submission: any) => {
          let ts = submission.lastSubmittedAt;
          if (ts && typeof ts === 'object' && typeof ts.toDate === 'function') {
            // Already a Firestore Timestamp
          } else if (ts && ts.seconds !== undefined && ts.nanoseconds !== undefined) {
            ts = new Timestamp(ts.seconds, ts.nanoseconds);
          } else if (typeof ts === 'number') {
            ts = Timestamp.fromMillis(ts);
          } else if (typeof ts === 'string') {
            const date = new Date(ts);
            if (!isNaN(date.getTime())) ts = Timestamp.fromDate(date);
          }
          
          const finalStatus = submission.status || 'pending';
          
          return {
            taskId,
            ...submission,
            status: finalStatus,
            timestamp: ts,
            createdAt: ts, // For compatibility
          };
        });
      });

      setSubmissions(arr);
    } catch (error) {
      console.error('Error loading submissions:', error);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadContests = () => {
    if (!auth.currentUser) return;

    setContestsLoading(true);
    const q = query(collection(db, 'contests_rewards'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const now = Date.now();
        const activeItems: ContestReward[] = [];

        snapshot.forEach((doc) => {
          const data = doc.data();
          
          if (!data.isActive) return;
          
          const startAt = (data.startAt && typeof data.startAt === 'object' && 'toMillis' in data.startAt)
            ? data.startAt.toMillis()
            : (typeof data.startAt === 'number' ? data.startAt : 0);
          const endAt = (data.endAt && typeof data.endAt === 'object' && 'toMillis' in data.endAt)
            ? data.endAt.toMillis()
            : (typeof data.endAt === 'number' ? data.endAt : 0);

          if (startAt <= now && now <= endAt) {
            activeItems.push({
              id: doc.id,
              ...data,
              startAt: data.startAt,
              endAt: data.endAt,
            } as ContestReward);
          }
        });

        activeItems.sort((a, b) => {
          const aEnd = (a.endAt && typeof a.endAt === 'object' && 'toMillis' in a.endAt) 
            ? a.endAt.toMillis() 
            : (typeof a.endAt === 'number' ? a.endAt : 0);
          const bEnd = (b.endAt && typeof b.endAt === 'object' && 'toMillis' in b.endAt) 
            ? b.endAt.toMillis() 
            : (typeof b.endAt === 'number' ? b.endAt : 0);
          return aEnd - bEnd;
        });

        setContests(activeItems);
        setContestsLoading(false);
      },
      (error) => {
        console.error('Error loading contests:', error);
        setContestsLoading(false);
      }
    );

    return () => unsubscribe();
  };

  const loadChallenges = () => {
    if (!auth.currentUser) return;

    setChallengesLoading(true);
    const allChallenges: Map<string, Challenge> = new Map();
    
    function updateChallenges() {
      const now = Date.now();
      const activeChallenges: Challenge[] = [];
      
      allChallenges.forEach((challenge) => {
        const startAt = (challenge.startAt && typeof challenge.startAt === 'object' && 'toMillis' in challenge.startAt)
          ? challenge.startAt.toMillis()
          : (typeof challenge.startAt === 'number' ? challenge.startAt : 0);
        const endAt = (challenge.endAt && typeof challenge.endAt === 'object' && 'toMillis' in challenge.endAt)
          ? challenge.endAt.toMillis()
          : (typeof challenge.endAt === 'number' ? challenge.endAt : 0);

        // Only show if challenge has started and hasn't ended
        if (startAt <= now && now <= endAt) {
          activeChallenges.push(challenge);
        }
      });

      // Sort by end date
      activeChallenges.sort((a, b) => {
        const aEnd = (a.endAt && typeof a.endAt === 'object' && 'toMillis' in a.endAt)
          ? a.endAt.toMillis()
          : (typeof a.endAt === 'number' ? a.endAt : 0);
        const bEnd = (b.endAt && typeof b.endAt === 'object' && 'toMillis' in b.endAt)
          ? b.endAt.toMillis()
          : (typeof b.endAt === 'number' ? b.endAt : 0);
        return aEnd - bEnd;
      });

      setChallenges(activeChallenges);
      setChallengesLoading(false);
    }
    
    // Load active challenges
    const qActive = query(collection(db, 'challenges'), where('status', '==', 'active'));
    const unsubActive = onSnapshot(
      qActive,
      (snapshot) => {
        snapshot.forEach((doc) => {
          const data = doc.data();
          allChallenges.set(doc.id, {
            id: doc.id,
            ...data,
            startAt: data.startAt,
            endAt: data.endAt,
          } as Challenge);
        });
        updateChallenges();
      },
      (error) => {
        console.error('Error loading active challenges:', error);
        setChallengesLoading(false);
      }
    );

    // Load scheduled challenges
    const qScheduled = query(collection(db, 'challenges'), where('status', '==', 'scheduled'));
    const unsubScheduled = onSnapshot(
      qScheduled,
      (snapshot) => {
        snapshot.forEach((doc) => {
          const data = doc.data();
          allChallenges.set(doc.id, {
            id: doc.id,
            ...data,
            startAt: data.startAt,
            endAt: data.endAt,
          } as Challenge);
        });
        updateChallenges();
      },
      (error) => {
        console.error('Error loading scheduled challenges:', error);
        setChallengesLoading(false);
      }
    );

    return () => {
      unsubActive();
      unsubScheduled();
    };
  };

  const loadChallengeProgress = () => {
    if (!auth.currentUser || challenges.length === 0) return;

    const unsubs: Array<() => void> = [];

    challenges.forEach((challenge) => {
      const progressId = `${challenge.id}_${auth.currentUser!.uid}`;
      const progressRef = doc(db, 'userChallengeProgress', progressId);
      
      const unsubscribe = onSnapshot(
        progressRef,
        (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            setChallengeProgress(prev => ({
              ...prev,
              [challenge.id]: {
                count: data.count || 0,
                requiredCount: data.requiredCount || challenge.requiredCount,
                status: data.status || 'in_progress'
              }
            }));
          } else {
            // No progress yet, set default
            setChallengeProgress(prev => ({
              ...prev,
              [challenge.id]: {
                count: 0,
                requiredCount: challenge.requiredCount,
                status: 'in_progress'
              }
            }));
          }
        },
        (error) => {
          console.error('Error loading challenge progress:', error);
        }
      );

      unsubs.push(unsubscribe);
    });

    return () => {
      unsubs.forEach(u => u());
    };
  };

  const filteredSubmissions = useMemo(() => {
    return submissions.filter(sub => {
      // Task type filter
      let isType = true;
      if (taskFilter === 'Photo Tasks') {
        isType = sub.taskType?.toLowerCase() === 'photo';
      } else if (taskFilter === 'Video Tasks') {
        isType = sub.taskType?.toLowerCase() === 'video';
      }
      
      // Time filter (using timestamp like mobile app)
      let isTime = true;
      const now = new Date();
      if (sub.timestamp?.toDate) {
        const subDate = sub.timestamp.toDate();
        if (timeFilter === 'This Month') {
          isTime = subDate.getMonth() === now.getMonth() && subDate.getFullYear() === now.getFullYear();
        } else if (timeFilter === '3 Months' || timeFilter === '6 Months' || timeFilter === '12 Months') {
          let monthsAgo = 2;
          if (timeFilter === '6 Months') monthsAgo = 5;
          if (timeFilter === '12 Months') monthsAgo = 11;
          const startDate = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1);
          isTime = subDate >= startDate && subDate <= now;
        }
      }
      
      return isType && isTime;
    });
  }, [submissions, taskFilter, timeFilter]);

  const stats = useMemo(() => {
    // Only count approved submissions for PIX calculation
    const approved = filteredSubmissions.filter(s => s.status === 'approved');
    
    // Calculate PIX earned from approved submissions in the filtered period
    const pixEarned = approved.reduce((sum, sub) => {
      // Get PIX reward from rewardAmount or reward field (same as mobile app)
      // Try multiple field names to match mobile app behavior
      const reward = sub.rewardAmount || sub.reward || 0;
      const rewardValue = typeof reward === 'number' ? reward : (typeof reward === 'string' ? parseFloat(reward) || 0 : 0);
      return sum + rewardValue;
    }, 0);

    return {
      totalSubmissions: filteredSubmissions.length,
      approvedSubmissions: approved.length,
      rejectedSubmissions: filteredSubmissions.filter(s => s.status === 'rejected').length,
      pendingSubmissions: filteredSubmissions.filter(s => s.status === 'pending').length,
      totalRewardEarned: pixEarned,
    };
  }, [filteredSubmissions]);

  const formatDate = (timestamp: Timestamp | number | undefined): string => {
    if (!timestamp) return '';
    const ms = (timestamp && typeof timestamp === 'object' && 'toMillis' in timestamp)
      ? timestamp.toMillis()
      : (typeof timestamp === 'number' ? timestamp : 0);
    if (ms === 0) return '';
    const date = new Date(ms);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isEndingSoon = (endAt: Timestamp | number): boolean => {
    if (!endAt) return false;
    const ms = (endAt && typeof endAt === 'object' && 'toMillis' in endAt)
      ? endAt.toMillis()
      : (typeof endAt === 'number' ? endAt : 0);
    const now = Date.now();
    const hoursUntilEnd = (ms - now) / (1000 * 60 * 60);
    return hoursUntilEnd > 0 && hoursUntilEnd <= 48;
  };

  const timeLeftLabel = (endAt: Timestamp | number): string => {
    if (!endAt) return '';
    const ms = (endAt && typeof endAt === 'object' && 'toMillis' in endAt)
      ? endAt.toMillis()
      : (typeof endAt === 'number' ? endAt : 0);
    const remaining = Math.max(0, ms - Date.now());
    const days = Math.floor(remaining / (24 * 3600 * 1000));
    const hours = Math.floor((remaining % (24 * 3600 * 1000)) / (3600 * 1000));
    return days > 0 ? `${days}d ${hours}h left` : `${hours}h left`;
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">Loading your stats...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container" style={{ background: colors.background, color: colors.text }}>
      <main className="dashboard-main">
        <h1 style={{ color: colors.text }}>Dashboard</h1>

        {/* Filters */}
        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group">
              <label style={{ color: colors.text }}>Time Period:</label>
              <div className="filter-buttons">
                {timeFilters.map(filter => (
                  <button
                    key={filter}
                    className={`filter-btn ${timeFilter === filter ? 'active' : ''}`}
                    onClick={() => setTimeFilter(filter)}
                    style={{
                      background: timeFilter === filter ? colors.primary : colors.card,
                      color: timeFilter === filter ? '#fff' : colors.text,
                      borderColor: timeFilter === filter ? colors.primary : colors.border,
                    }}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-group">
              <label style={{ color: colors.text }}>Task Type:</label>
              <div className="filter-buttons">
                {taskFilters.map(filter => (
                  <button
                    key={filter}
                    className={`filter-btn ${taskFilter === filter ? 'active' : ''}`}
                    onClick={() => setTaskFilter(filter)}
                    style={{
                      background: taskFilter === filter ? colors.primary : colors.card,
                      color: taskFilter === filter ? '#fff' : colors.text,
                      borderColor: taskFilter === filter ? colors.primary : colors.border,
                    }}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card" style={{ background: colors.card, borderColor: colors.border }}>
            <div className="stat-label" style={{ color: colors.secondary }}>Total Submissions</div>
            <div className="stat-value" style={{ color: colors.text }}>{stats.totalSubmissions}</div>
          </div>

          <div className="stat-card stat-approved" style={{ background: colors.card, borderColor: colors.border }}>
            <div className="stat-label" style={{ color: colors.secondary }}>Approved</div>
            <div className="stat-value" style={{ color: colors.success }}>{stats.approvedSubmissions}</div>
          </div>

          <div className="stat-card stat-pending" style={{ background: colors.card, borderColor: colors.border }}>
            <div className="stat-label" style={{ color: colors.secondary }}>Pending</div>
            <div className="stat-value" style={{ color: colors.warning }}>{stats.pendingSubmissions}</div>
          </div>

          <div className="stat-card stat-rejected" style={{ background: colors.card, borderColor: colors.border }}>
            <div className="stat-label" style={{ color: colors.secondary }}>Rejected</div>
            <div className="stat-value" style={{ color: colors.error }}>{stats.rejectedSubmissions}</div>
          </div>

          <div className="stat-card stat-rewards" style={{ background: colors.card, borderColor: colors.border }}>
            <div className="stat-label" style={{ color: colors.secondary }}>$PIX Earned</div>
            <div className="stat-value" style={{ color: colors.primary }}>
              {Math.round(stats.totalRewardEarned)}
            </div>
          </div>
        </div>

        {/* Challenges Section */}
        <div className="challenges-section" style={{ background: colors.card, borderColor: colors.border }}>
          <h2 style={{ color: colors.text }}>Challenges</h2>
          {challengesLoading ? (
            <div className="loading">Loading challenges...</div>
          ) : challenges.length === 0 ? (
            <div className="empty-state">No active challenges at this time.</div>
          ) : (
            <div
              className={`challenges-grid ${
                challenges.length >= 3 ? 'carousel' : 'centered'
              }`}
            >
              {challenges.map((challenge) => {
                const progress = challengeProgress[challenge.id];
                const count = progress?.count ?? 0;
                const required = progress?.requiredCount ?? challenge.requiredCount;
                const progressPercent = Math.min(100, required ? (count / required) * 100 : 0);
                const endingSoon = isEndingSoon(challenge.endAt);

                return (
                  <div 
                    key={challenge.id} 
                    className="challenge-card"
                    style={{ 
                      background: theme.mode === 'dark' ? '#1a1a1a' : colors.card,
                      borderColor: colors.border,
                      color: colors.text
                    }}
                  >
                    <div className="challenge-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {challenge.imageUrl ? (
                          <img 
                            src={challenge.imageUrl} 
                            alt={challenge.title}
                            style={{
                              width: '56px',
                              height: '56px',
                              borderRadius: '28px',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '28px',
                            background: colors.primary,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '24px'
                          }}>
                            🎯
                          </div>
                        )}
                        <div style={{ flex: 1 }}>
                          <h3 style={{ color: colors.text, margin: 0, fontSize: '18px' }}>
                            {challenge.title}
                          </h3>
                        </div>
                      </div>
                    </div>
                    
                    <div 
                      className="challenge-details"
                      style={{ background: colors.background, borderColor: colors.border }}
                    >
                      <div className="challenge-detail">
                        <span className="detail-label" style={{ color: colors.secondary }}>
                          Progress:
                        </span>
                        <span className="detail-value" style={{ color: colors.text }}>
                          {count}/{required}
                        </span>
                      </div>
                      <div className="challenge-detail">
                        <span className="detail-label" style={{ color: colors.secondary }}>
                          Reward:
                        </span>
                        <span className="detail-value" style={{ color: colors.primary, fontWeight: 'bold' }}>
                          +{challenge.pixReward} PIX
                        </span>
                      </div>
                      <div className="challenge-detail">
                        <span className="detail-label" style={{ color: colors.secondary }}>
                          Ends:
                        </span>
                        <span 
                          className={`detail-value ${endingSoon ? 'ending-soon' : ''}`}
                          style={{ 
                            color: endingSoon ? colors.error : colors.text 
                          }}
                        >
                          {formatDate(challenge.endAt)}
                          {endingSoon && ' (Ending Soon!)'}
                        </span>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div style={{
                      height: '8px',
                      borderRadius: '9999px',
                      background: colors.border,
                      overflow: 'hidden',
                      marginBottom: '12px'
                    }}>
                      <div style={{
                        width: `${progressPercent}%`,
                        height: '100%',
                        background: colors.primary,
                        transition: 'width 0.3s'
                      }} />
                    </div>

                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '16px',
                      fontSize: '12px',
                      color: colors.secondary
                    }}>
                      <span>{timeLeftLabel(challenge.endAt)}</span>
                      <span>
                        {progress?.status === 'rewarded' ? 'Completed' : 'In progress'}
                      </span>
                    </div>

                    <Link 
                      to={`/challenges/${challenge.id}`}
                      className="challenge-button"
                      style={{
                        background: colors.primary,
                        color: '#fff'
                      }}
                    >
                      View Challenge
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Contests & Discounts */}
        <div className="contests-section" style={{ background: colors.card, borderColor: colors.border }}>
          <h2 style={{ color: colors.text }}>Contests & Discounts</h2>
          {contestsLoading ? (
            <div className="loading">Loading contests...</div>
          ) : contests.length === 0 ? (
            <div className="empty-state">No active contests or discounts at this time.</div>
          ) : (
            <div
              className={`contests-grid ${
                contests.length >= 3 ? 'carousel' : 'centered'
              }`}
            >
              {contests.map((contest) => {
                const endingSoon = isEndingSoon(contest.endAt);
                return (
                  <div 
                    key={contest.id} 
                    className="contest-card"
                    style={{ 
                      background: theme.mode === 'dark' ? '#1a1a1a' : colors.card,
                      borderColor: colors.border,
                      color: colors.text
                    }}
                  >
                    <div className="contest-header">
                      <div>
                        <h3 style={{ color: colors.text }}>{contest.title}</h3>
                        {contest.partnerName && (
                          <p className="partner-name" style={{ color: colors.secondary }}>
                            {contest.partnerName}
                          </p>
                        )}
                      </div>
                      <span 
                        className="contest-type"
                        style={{
                          background: colors.primary,
                          color: '#fff'
                        }}
                      >
                        {contest.type === 'contest' ? 'Contest' : 'Discount'}
                      </span>
                    </div>
                    
                    <p className="contest-description" style={{ color: colors.secondary }}>
                      {contest.description}
                    </p>
                    
                    <div 
                      className="contest-details"
                      style={{ background: colors.background, borderColor: colors.border }}
                    >
                      <div className="contest-detail">
                        <span className="detail-label" style={{ color: colors.secondary }}>
                          PIX Required:
                        </span>
                        <span className="detail-value" style={{ color: colors.text }}>
                          {contest.pixRequired}
                        </span>
                      </div>
                      <div className="contest-detail">
                        <span className="detail-label" style={{ color: colors.secondary }}>
                          Ends:
                        </span>
                        <span 
                          className={`detail-value ${endingSoon ? 'ending-soon' : ''}`}
                          style={{ 
                            color: endingSoon ? colors.error : colors.text 
                          }}
                        >
                          {formatDate(contest.endAt)}
                          {endingSoon && ' (Ending Soon!)'}
                        </span>
                      </div>
                    </div>

                    <Link 
                      to={`/contests/${contest.id}`}
                      className="contest-button"
                      style={{
                        background: colors.primary,
                        color: '#fff'
                      }}
                    >
                      {contest.type === 'contest' ? 'Enter Contest' : 'Redeem Discount'}
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
